import { SettingsCard } from './SettingsCard';
import { SettingsToggle } from './SettingsToggle';
import { settingRowStyle, settingLabelColumnStyle, settingLabelStyle, settingsDescriptionStyle, settingsInputStyle, settingsPrimaryButtonStyle } from './settingsStyles';
// Organisation navigation policy settings panel.
//
// Lets an org admin (Users:manage) configure the versioned navigation policy:
// an explicit activate / validate-only choice, a preset with transparent
// individual operation toggles, the explicit publishing-QC and intake-approval
// choices, and per-criterion/per-region company treatment (evaluated /
// not-evaluated) shown ALONGSIDE — never replacing — each regulator's own
// severity and source.
//
// Layout: criteria are grouped by their human-readable label ONCE, with the
// applicable regions shown in brackets. The differing per-regulator obligations
// (severity, source, version, qualification, company toggle) live in an
// expandable regional detail per criterion — they are never collapsed into one
// universal rule, and an applicable-region list never claims regions that are
// not actually mapped.
//
// Honesty the UI must preserve:
//   • The default is validate-only; nothing here activates mutation implicitly.
//   • "Applies to" tags are applicability, never "meets"/certified.
//   • Unqualified-inheritance (MHRA) and unsupported profiles are shown as such,
//     never as a qualified preset, and never counted among a criterion's regions.
//   • Configuring the policy here records intent; saving does not mutate any
//     uploaded file. Generated/edited drafts stay review-only until qualification
//     is recorded. The compiler enforcement wiring is being integrated and is not
//     claimed verified here.
//   • Legacy personal link toggles only narrow styling within org policy; they
//     are not a separate authoriser and never activated org mutation — disclosed.

import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { formatApiError } from '@/lib/errors';
import { ApiError, parseApiErrorBody } from '@/api/v2Client';
import {
  NAVIGATION_OPERATIONS,
  navigationPolicy,
  type CriterionMappingWire,
  type CriterionSpecWire,
  type DisabledCriterionWire,
  type IntakeApprovalChoice,
  type NavigationCatalogue,
  type NavigationOperation,
  type NavigationPolicyChangeBody,
  type NavigationPolicyWire,
  type NavigationPreset,
  type ProfileMetaWire,
  type PublishingQcChoice,
} from '@/api/navigationPolicy';

const subtleStyle = settingsDescriptionStyle;
const groupStyle: CSSProperties = {
  border: '1px solid var(--border, #ddd)',
  borderRadius: 6,
  padding: 12,
  marginTop: 12,
};
const disclosureStyle: CSSProperties = {
  background: 'var(--amber-bg, #fff8e1)',
  border: '1px solid var(--amber, #e0b000)',
  borderRadius: 4,
  padding: '8px 12px',
  fontSize: 12,
  marginTop: 8,
};
const errorStyle: CSSProperties = {
  color: 'var(--red, #b00020)',
  background: 'var(--red-bg, #fdecee)',
  borderRadius: 4,
  padding: '8px 12px',
  fontSize: 13,
  marginTop: 8,
};
const criterionRowStyle: CSSProperties = {
  border: '1px solid var(--border, #e5e5e5)',
  borderRadius: 6,
  padding: '8px 10px',
  marginTop: 8,
};
const summaryStyle: CSSProperties = {
  cursor: 'pointer',
  listStyle: 'revert',
  userSelect: 'none',
};
const regionTagStyle: CSSProperties = {
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 3,
  background: 'var(--surface2, #f0f0f0)',
  color: 'var(--text, #222)',
  marginRight: 4,
};
const regionDetailStyle: CSSProperties = {
  borderTop: '1px solid var(--border, #eee)',
  padding: '6px 0',
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
};
const severityStyle = (sev: string): CSSProperties => ({
  fontSize: 11,
  fontWeight: 700,
  padding: '1px 6px',
  borderRadius: 3,
  color: sev === 'error' ? '#fff' : 'var(--text, #222)',
  background: sev === 'error' ? 'var(--red, #b00020)' : sev === 'warning' ? 'var(--amber, #e0b000)' : 'var(--border, #e5e5e5)',
});

const OPERATION_LABELS: Record<NavigationOperation, string> = {
  createBookmark: 'Generate missing bookmarks',
  createHyperlink: 'Generate missing hyperlinks',
  modifyBookmark: 'Modify existing bookmarks',
  modifyHyperlink: 'Modify existing hyperlinks',
  style: 'Apply link styling',
  manual: 'Authorised manual edits',
  linkExistingToc: 'Link an existing table of contents',
  formatConversion: 'Format conversion',
};

// Readable preset names shown to the admin (the raw tokens — automatic /
// preserve / custom — are the contract, never the dominant label).
const PRESET_LABELS: Record<NavigationPreset, string> = {
  automatic: 'Automatic — generate missing bookmarks & hyperlinks',
  preserve: 'Preserve — keep uploaded files, make no changes',
  custom: 'Custom — choose each operation',
};

// Short, readable region labels for the applicable-region brackets. Display
// only; falls back to the profile's own label, then the raw id, so an
// unmapped/new profile id is never silently dropped.
const REGION_SHORT: Record<string, string> = {
  ICH: 'ICH',
  FDA: 'FDA',
  EU: 'EU',
  HC: 'Health Canada',
  TGA: 'TGA',
  SWISSMEDIC: 'Swissmedic',
  PMDA_JP: 'PMDA Japan',
  MHRA: 'MHRA',
};

// Human-readable gloss for the profile's evidence level. The raw token is kept
// alongside so the exact qualification state is never hidden.
const EVIDENCE_LABELS: Record<string, string> = {
  'researched-validator': 'Researched against a formal validator ruleset',
  'researched-guidance': 'Researched from primary specification/guidance',
  'guidance-mapping': 'Guidance mapping (not validator-qualified)',
  'unqualified-inheritance': 'Unqualified inheritance only',
  unsupported: 'Unsupported — no researched evidence',
};

// Display-only mirror of the backend presets (the server recomputes
// authoritatively on save). `style` is OFF under Automatic: styling is a
// separate, explicit decision (set via Custom), NOT part of the suggested
// automatic missing-item generation preset — keep in lock-step with
// AUTOMATIC_OPERATIONS in src/services/navigation/policy.ts.
const AUTOMATIC_OPS: Record<NavigationOperation, boolean> = {
  createBookmark: true,
  createHyperlink: true,
  modifyBookmark: false,
  modifyHyperlink: false,
  style: false,
  manual: true,
  linkExistingToc: true,
  formatConversion: false,
};
const ALL_OFF_OPS: Record<NavigationOperation, boolean> = {
  createBookmark: false,
  createHyperlink: false,
  modifyBookmark: false,
  modifyHyperlink: false,
  style: false,
  manual: false,
  linkExistingToc: false,
  formatConversion: false,
};

function disabledKey(criterionId: string, profile?: string): string {
  return `${criterionId}|${profile ?? '*'}`;
}

function regionShort(profile: string, metaById: Map<string, ProfileMetaWire>): string {
  return REGION_SHORT[profile] ?? metaById.get(profile)?.label ?? profile;
}

interface NavigationPolicyPanelProps {
  readonly linkPreferences?: ReactNode;
}

export function NavigationPolicyPanel({ linkPreferences }: NavigationPolicyPanelProps = {}): ReactElement {
  const perms = useUserPermissions();
  const canWriteOrg = !perms.loading && perms.hasPermission('Users', 'manage');

  const [policy, setPolicy] = useState<NavigationPolicyWire | null>(null);
  const [catalogue, setCatalogue] = useState<NavigationCatalogue | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);

  // Draft edit state.
  const [mode, setMode] = useState<'activate' | 'validate-only'>('validate-only');
  const [preset, setPreset] = useState<NavigationPreset>('automatic');
  const [publishingQc, setPublishingQc] = useState<PublishingQcChoice | ''>('');
  const [intakeApproval, setIntakeApproval] = useState<IntakeApprovalChoice | ''>('');
  const [customOps, setCustomOps] = useState<Record<NavigationOperation, boolean>>(ALL_OFF_OPS);
  const [disabled, setDisabled] = useState<Set<string>>(new Set());

  const hydrate = useCallback((p: NavigationPolicyWire) => {
    setPolicy(p);
    setMode(p.activated ? 'activate' : 'validate-only');
    setPreset(p.preset);
    setPublishingQc(p.activated ? p.publishingQc : '');
    setIntakeApproval(p.activated ? p.intakeApproval : '');
    setCustomOps({ ...ALL_OFF_OPS, ...p.operations });
    setDisabled(new Set(p.disabledCriteria.map((d) => disabledKey(d.criterionId, d.profile))));
  }, []);

  const load = useCallback(() => {
    setLoadError(null);
    navigationPolicy
      .get()
      .then((res) => {
        setCatalogue(res.catalogue);
        hydrate(res.policy);
      })
      .catch((e: unknown) => setLoadError(formatApiError(e, 'Failed to load navigation policy')));
  }, [hydrate]);

  useEffect(() => {
    load();
  }, [load]);

  const previewOps = useMemo<Record<NavigationOperation, boolean>>(() => {
    if (mode === 'validate-only') return ALL_OFF_OPS;
    if (preset === 'automatic') return AUTOMATIC_OPS;
    if (preset === 'preserve') return ALL_OFF_OPS;
    return customOps;
  }, [mode, preset, customOps]);

  const needsExplicitChoice = mode === 'activate' && (publishingQc === '' || intakeApproval === '');

  const handleSave = useCallback(async () => {
    if (!policy) return;
    setSaveError(null);
    setConflict(false);
    if (needsExplicitChoice) {
      setSaveError('Activation requires an explicit publishing QC choice and an intake approval choice.');
      return;
    }
    const disabledCriteria: DisabledCriterionWire[] = [...disabled].map((k) => {
      const [criterionId, profile] = k.split('|');
      return profile && profile !== '*' ? { criterionId: criterionId!, profile } : { criterionId: criterionId! };
    });
    const change: NavigationPolicyChangeBody =
      mode === 'activate'
        ? {
            kind: 'activate',
            preset,
            publishingQc: publishingQc as PublishingQcChoice,
            intakeApproval: intakeApproval as IntakeApprovalChoice,
            ...(preset === 'custom' ? { customOperations: customOps } : {}),
            disabledCriteria,
          }
        : { kind: 'validate-only', disabledCriteria };
    setSaving(true);
    try {
      const res = await navigationPolicy.update({ expectedVersion: policy.version, change });
      hydrate(res.policy);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setConflict(true);
        setSaveError(
          parseApiErrorBody(e)?.message ??
            'The policy was changed by someone else while you were editing. Reload to see the current version.',
        );
      } else {
        setSaveError(formatApiError(e, 'Failed to save navigation policy'));
      }
    } finally {
      setSaving(false);
    }
  }, [policy, needsExplicitChoice, disabled, mode, preset, publishingQc, intakeApproval, customOps, hydrate]);

  // ----- Catalogue derivations (criterion-first grouping) -----
  const metaById = useMemo(() => {
    const m = new Map<string, ProfileMetaWire>();
    for (const p of catalogue?.profiles ?? []) m.set(p.profile, p);
    return m;
  }, [catalogue]);

  const mappingsByCriterion = useMemo(() => {
    const m = new Map<string, CriterionMappingWire[]>();
    for (const mapping of catalogue?.mappings ?? []) {
      const arr = m.get(mapping.criterionId) ?? [];
      arr.push(mapping);
      m.set(mapping.criterionId, arr);
    }
    return m;
  }, [catalogue]);

  const regionalCriteria = useMemo(
    () => (catalogue?.criteria ?? []).filter((c) => c.kind === 'regional'),
    [catalogue],
  );
  const structuralCriteria = useMemo(
    () => (catalogue?.criteria ?? []).filter((c) => c.kind === 'structural-integrity'),
    [catalogue],
  );

  // Profiles that carry NO evaluated criteria (unqualified inheritance,
  // unsupported, or simply unmapped) — surfaced honestly, never as a region
  // that "meets" anything.
  const honestProfiles = useMemo(() => {
    const mappedProfiles = new Set((catalogue?.mappings ?? []).map((m) => m.profile));
    return (catalogue?.profiles ?? []).filter((p) => !mappedProfiles.has(p.profile));
  }, [catalogue]);

  if (loadError) {
    return (
      <SettingsCard title="Bookmarks & hyperlinks">
        {linkPreferences}
        <div role="alert" style={errorStyle} data-testid="nav-policy-load-error">
          {loadError}
        </div>
        <button type="button" style={settingsPrimaryButtonStyle()} onClick={load}>
          Retry
        </button>
      </SettingsCard>
    );
  }
  if (!policy || !catalogue) {
    return (
      <SettingsCard title="Bookmarks & hyperlinks">
        {linkPreferences}
        <p>Loading…</p>
      </SettingsCard>
    );
  }

  const inputsDisabled = !canWriteOrg || saving;

  return (
    <SettingsCard
      title="Bookmarks & hyperlinks"
      actions={
        <button
          type="button"
          style={settingsPrimaryButtonStyle(inputsDisabled || needsExplicitChoice)}
          data-testid="nav-policy-save"
          aria-label="save-navigation-policy"
          disabled={inputsDisabled || needsExplicitChoice}
          title={needsExplicitChoice ? 'Choose the publishing QC and intake approval before activating' : undefined}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save navigation policy'}
        </button>
      }
      testId="navigation-policy-panel"
      description={<>
        Organisation-wide, versioned policy for bookmark and hyperlink handling. Current version:{' '}
        <strong data-testid="nav-policy-version">{policy.version}</strong> —{' '}
        <strong>{policy.activated ? 'activated' : 'validate-only (default)'}</strong>.
      </>}
    >
      {linkPreferences}
      <h4 style={{ ...settingLabelStyle, margin: '20px 0 8px' }}>Organisation navigation policy</h4>

      {!canWriteOrg && (
        <div style={disclosureStyle} data-testid="nav-policy-readonly-note">
          You can view the navigation policy but changing it requires the <code>Users:manage</code> permission.
        </div>
      )}

      {/* Neutral, accurate statement of what saving this policy does. */}
      <div style={disclosureStyle} data-testid="nav-policy-enforcement-disclosure">
        This policy controls which operations are allowed. Generated/edited drafts remain review-only until
        independent reader and regional validator checks are complete. Saving policy does not change uploaded files.
        Each submission records its policy before navigation changes are made.
      </div>

      {/* Legacy personal-settings disclosure. */}
      <div style={disclosureStyle} data-testid="nav-policy-legacy-disclosure">
        Personal link appearance preferences only narrow styling within this organisation policy — they are
        not a separate authoriser for document changes. Activation is the explicit choice below.
      </div>

      {/* Activation choice. */}
      <div style={groupStyle}>
        <strong>Activation</strong>
        <div style={{ marginTop: 6 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>
            <input
              type="radio"
              name="nav-policy-mode"
              aria-label="mode-validate-only"
              checked={mode === 'validate-only'}
              disabled={inputsDisabled}
              onChange={() => setMode('validate-only')}
            />{' '}
            Validate only — never modify documents (default)
          </label>
          <label style={{ display: 'block' }}>
            <input
              type="radio"
              name="nav-policy-mode"
              aria-label="mode-activate"
              checked={mode === 'activate'}
              disabled={inputsDisabled}
              onChange={() => setMode('activate')}
            />{' '}
            Activate navigation operations
          </label>
        </div>
      </div>

      {mode === 'activate' && (
        <div style={groupStyle} data-testid="nav-policy-activation-config">
          <strong>Preset</strong>
          <div style={{ marginTop: 6 }}>
            {(['automatic', 'preserve', 'custom'] as const).map((p) => (
              <label key={p} style={{ display: 'block', marginBottom: 4 }}>
                <input
                  type="radio"
                  name="nav-policy-preset"
                  aria-label={`preset-${p}`}
                  checked={preset === p}
                  disabled={inputsDisabled}
                  onChange={() => setPreset(p)}
                />{' '}
                {PRESET_LABELS[p]}
              </label>
            ))}
          </div>

          <div style={{ marginTop: 10 }}>
            <strong>Operations</strong>{' '}
            <span style={subtleStyle}>
              {preset === 'custom' ? 'set each switch explicitly' : 'preset values (edit via Custom)'}
            </span>
            {NAVIGATION_OPERATIONS.map((op) => (
              <div key={op} style={settingRowStyle}>
                <div style={settingLabelColumnStyle}>
                  <span style={settingLabelStyle}>{OPERATION_LABELS[op]}</span>
                </div>
                <SettingsToggle
                  label={`operation-${op}`}
                  testId={`operation-${op}`}
                  checked={previewOps[op]}
                  disabled={inputsDisabled || preset !== 'custom'}
                  onChange={(checked) => setCustomOps((prev) => ({ ...prev, [op]: checked }))}
                />
              </div>
            ))}
          </div>

          {/* Explicit, no-hidden-default QC and intake choices — "None" stays
              an explicit, visible option, never a silent default. */}
          <div style={{ marginTop: 10 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>
              Publishing QC gate (explicit choice required):{' '}
              <select
                style={settingsInputStyle}
                aria-label="publishing-qc"
                value={publishingQc}
                disabled={inputsDisabled}
                onChange={(e) => setPublishingQc(e.target.value as PublishingQcChoice | '')}
              >
                <option value="">— choose —</option>
                <option value="none">None (no publishing QC gate)</option>
                <option value="designated">Designated reviewer</option>
                <option value="dual">Dual review</option>
              </select>
            </label>
            <label style={{ display: 'block' }}>
              Intake approval requirement (explicit choice required):{' '}
              <select
                style={settingsInputStyle}
                aria-label="intake-approval"
                value={intakeApproval}
                disabled={inputsDisabled}
                onChange={(e) => setIntakeApproval(e.target.value as IntakeApprovalChoice | '')}
              >
                <option value="">— choose —</option>
                <option value="none">None (no intake approval required)</option>
                <option value="attestation">Operator attestation</option>
                <option value="verified-record">Verified external record</option>
              </select>
            </label>
          </div>
        </div>
      )}

      {/* Criteria catalogue — grouped by criterion ONCE; the applicable regions
          appear in brackets, and each regulator's severity/source/version/
          qualification plus the company toggle live in the expandable detail. */}
      <div style={groupStyle} data-testid="nav-policy-criteria">
        <strong>Criteria</strong>
        <p style={subtleStyle}>
          Each criterion is listed once, with the regions whose cited source states it in brackets — this is
          applicability, not a claim of compliance, and not a universal requirement. Expand a criterion to see each
          regulator&apos;s severity, source and qualification, and to toggle whether your organisation evaluates it per
          region. A disabled criterion is reported as not-evaluated, never a silent pass. Structural package-integrity
          checks cannot be disabled.
        </p>

        {regionalCriteria.map((spec) => (
          <RegionalCriterionRow
            key={spec.id}
            spec={spec}
            mappings={mappingsByCriterion.get(spec.id) ?? []}
            metaById={metaById}
            disabled={disabled}
            inputsDisabled={inputsDisabled}
            onToggle={(profile, evaluate) =>
              setDisabled((prev) => {
                const next = new Set(prev);
                const key = disabledKey(spec.id, profile);
                if (evaluate) next.delete(key);
                else next.add(key);
                return next;
              })
            }
          />
        ))}

        {structuralCriteria.length > 0 && (
          <div style={{ marginTop: 14 }} data-testid="nav-policy-structural">
            <strong style={{ fontSize: 13 }}>Non-waivable integrity checks</strong>
            <p style={subtleStyle}>
              Structural technical checks. These always run and cannot be disabled or exempted by company policy or an
              exception — they are not configurable regulatory criteria.
            </p>
            {structuralCriteria.map((spec) => (
              <div
                key={spec.id}
                style={criterionRowStyle}
                data-testid={`criterion-structural-${spec.id}`}
              >
                <div style={{ fontWeight: 600 }}>
                  {spec.title} <span style={{ ...regionTagStyle, background: 'var(--border, #e5e5e5)' }}>always on</span>
                </div>
                <div style={subtleStyle}>Integrity check · {spec.id}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Optional / unqualified profiles — honest "unknown", never a region
          that meets anything and never counted among a criterion's regions. */}
      {honestProfiles.length > 0 && (
        <div style={groupStyle} data-testid="nav-policy-honest-profiles">
          <strong>Optional / unqualified profiles</strong>
          <p style={subtleStyle}>
            These profiles have no evaluated criteria in the current catalogue. They are shown honestly and are never
            presented as a qualified preset.
          </p>
          {honestProfiles.map((p) => (
            <div key={p.profile} style={{ marginTop: 8 }} data-testid={`nav-profile-honest-${p.profile}`}>
              <div style={{ fontWeight: 600 }}>
                {p.label} <span style={subtleStyle}>{p.version}</span>{' '}
                <span style={subtleStyle} data-testid={`nav-profile-evidence-${p.profile}`}>
                  ({EVIDENCE_LABELS[p.evidenceLevel] ?? p.evidenceLevel} · {p.evidenceLevel})
                </span>
              </div>
              <div style={subtleStyle} data-testid={`nav-profile-no-criteria-${p.profile}`}>
                {p.inheritsFrom
                  ? `Inherits ${regionShort(p.inheritsFrom, metaById)} format (unqualified); no evaluated criteria.`
                  : 'No evaluated criteria for this profile.'}
              </div>
              {p.advisory && <div style={subtleStyle}>{p.advisory}</div>}
            </div>
          ))}
        </div>
      )}

      {saveError && (
        <div role="alert" style={errorStyle} data-testid="nav-policy-save-error">
          {saveError}
          {conflict && (
            <>
              {' '}
              <button type="button" onClick={load} data-testid="nav-policy-reload">
                Reload
              </button>
            </>
          )}
        </div>
      )}
    </SettingsCard>
  );
}

interface RegionalCriterionRowProps {
  readonly spec: CriterionSpecWire;
  readonly mappings: readonly CriterionMappingWire[];
  readonly metaById: Map<string, ProfileMetaWire>;
  readonly disabled: ReadonlySet<string>;
  readonly inputsDisabled: boolean;
  readonly onToggle: (profile: string, evaluate: boolean) => void;
}

function RegionalCriterionRow({
  spec,
  mappings,
  metaById,
  disabled,
  inputsDisabled,
  onToggle,
}: RegionalCriterionRowProps): ReactElement {
  const regulatoryMappings = mappings.filter((m) => !m.internalQuality);
  const isInternalOnly = mappings.length > 0 && regulatoryMappings.length === 0;
  const notEvaluated = mappings.filter((m) => disabled.has(disabledKey(m.criterionId, m.profile)));
  const treatment =
    mappings.length === 0
      ? 'No applicable regions in the current catalogue.'
      : notEvaluated.length === 0
        ? isInternalOnly ? 'Company: quality check enabled for the configured profiles' : 'Company: evaluated in all applicable regions'
        : `Company: not evaluated in ${notEvaluated
            .map((m) => regionShort(m.profile, metaById))
            .join(', ')} (reported as not-evaluated, never a pass)`;

  return (
    // Uncontrolled <details>: the company-treatment summary (including any
    // not-evaluated regions) is always visible in the <summary>, so an
    // exception is never hidden even while the per-region detail is collapsed.
    <details style={criterionRowStyle} data-testid={`criterion-${spec.id}`}>
      <summary style={summaryStyle}>
        <span style={{ fontWeight: 600 }}>{spec.title}</span>{' '}
        {spec.defaultEnabled && (
          <span style={{ ...regionTagStyle, background: 'var(--accent-bg, #e6f4ec)' }}>enabled by default</span>
        )}
        <span data-testid={`criterion-regions-${spec.id}`}>
          {' ['}
          {isInternalOnly
            ? 'AutoeCTD quality check — no regional requirement asserted'
            : regulatoryMappings.length === 0
              ? 'no mapped regions'
              : regulatoryMappings.map((m) => regionShort(m.profile, metaById)).join(' · ')}
          {'] '}
        </span>
        <div style={subtleStyle} data-testid={`criterion-treatment-${spec.id}`}>
          {treatment} · {spec.id}
        </div>
      </summary>

      {mappings.map((m) => {
        const meta = metaById.get(m.profile);
        const key = disabledKey(m.criterionId, m.profile);
        const evaluated = !disabled.has(key);
        return (
          <div key={key} style={regionDetailStyle} data-testid={`criterion-${m.profile}-${m.criterionId}`}>
            <input
              type="checkbox"
              aria-label={`evaluated-${m.profile}-${m.criterionId}`}
              checked={evaluated}
              disabled={inputsDisabled}
              onChange={(e) => onToggle(m.profile, e.target.checked)}
            />
            <div style={{ flex: 1 }}>
              <div>
                <strong>{regionShort(m.profile, metaById)}</strong>{' '}
                <span style={subtleStyle}>{m.source.version}</span>{' '}
                <span style={severityStyle(m.severity)}>{m.severity}</span>{' '}
                <span style={subtleStyle}>
                  {m.internalQuality ? 'AutoeCTD quality check, not a regional obligation' : m.authorityLevel === 'formal-validation' ? 'formal validation rule' : 'guidance recommendation'}
                </span>{' '}
                <span style={subtleStyle}>· company: {evaluated ? 'evaluated' : 'not-evaluated'}</span>
              </div>
              <div style={subtleStyle}>
                {m.internalQuality ? 'Profile context (source does not require this check)' : 'Applies to'}: {meta?.label ?? m.profile} · {m.source.specification} {m.source.version} §
                {m.source.section}
                {m.ruleIds.length > 0 ? ` · rules ${m.ruleIds.join(', ')}` : ''}
                {m.pageThreshold !== null ? ` · ${m.thresholdBasis ?? 'threshold'} ≥ ${m.pageThreshold}` : ''}
                {m.automatedCriterionRemoved ? ' · automated criterion removed (narrative kept)' : ''}
                {m.requiresJustification ? ' · requires warnings.xml justification' : ''}
              </div>
              {meta && (
                <div style={subtleStyle} data-testid={`criterion-qual-${m.profile}-${m.criterionId}`}>
                  Qualification: {EVIDENCE_LABELS[meta.evidenceLevel] ?? meta.evidenceLevel} ({meta.evidenceLevel})
                  {meta.qualifiedPreset ? '' : ' · not a qualified preset'}
                  {m.source.url ? (
                    <>
                      {' · '}
                      <a href={m.source.url} target="_blank" rel="noreferrer noopener">
                        source
                      </a>
                    </>
                  ) : null}
                </div>
              )}
              {m.notes && <div style={subtleStyle}>{m.notes}</div>}
            </div>
          </div>
        );
      })}
    </details>
  );
}
